// ABOUTME: TimeInput wrapper that adds a scrollable time dropdown with 30-min interval options.
// ABOUTME: Drop-in replacement for TimeInput — renders a portal dropdown on focus.
import {Button, Icon, TimeInput, TimeInputProps} from '@hm/fundamental'
import {mdiCheck} from '@mdi/js'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'

const TIME_OPTIONS: string[] = Array.from({length: 48}, (_, i) => {
  const hours = String(Math.floor(i / 2)).padStart(2, '0')
  const minutes = i % 2 === 0 ? '00' : '30'
  return `${hours}:${minutes}`
})

const DROPDOWN_HEIGHT = 240

function findNearestOptionIndex(value: string): number {
  if (!value) return 0
  const trimmed = value.trim()
  const exactIndex = TIME_OPTIONS.indexOf(trimmed)
  if (exactIndex >= 0) return exactIndex

  const parts = trimmed.split(':')
  const hours = parseInt(parts[0], 10)
  if (isNaN(hours)) return 0

  const minutes = parts.length > 1 ? parseInt(parts[1], 10) : 0
  const totalMinutes = hours * 60 + (isNaN(minutes) ? 0 : minutes)

  let closest = 0
  let minDiff = Infinity
  for (let i = 0; i < TIME_OPTIONS.length; i++) {
    const optMinutes = Math.floor(i / 2) * 60 + (i % 2 === 0 ? 0 : 30)
    const diff = Math.abs(optMinutes - totalMinutes)
    if (diff < minDiff) {
      minDiff = diff
      closest = i
    }
  }
  return closest
}

export const TimePickerInput: React.FC<TimeInputProps> = ({
  value = '',
  onChange,
  onFocus,
  onBlur,
  isDisabled,
  ...rest
}) => {
  const anchorRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const [position, setPosition] = useState({top: 0, left: 0, width: 120})

  const updatePosition = useCallback(() => {
    if (!anchorRef.current) return
    const rect = anchorRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const goAbove = spaceBelow < DROPDOWN_HEIGHT && rect.top > DROPDOWN_HEIGHT

    setPosition({
      top: goAbove ? rect.top - DROPDOWN_HEIGHT - 4 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    })
  }, [])

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsOpen(true)
      setFocusedIndex(findNearestOptionIndex(value))
      updatePosition()
      onFocus?.(e)
    },
    [onFocus, updatePosition, value]
  )

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsOpen(false)
      setFocusedIndex(-1)
      onBlur?.(e)
    },
    [onBlur]
  )

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen) return
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setFocusedIndex(prev => Math.min(prev + 1, TIME_OPTIONS.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setFocusedIndex(prev => Math.max(prev - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        if (focusedIndex >= 0 && focusedIndex < TIME_OPTIONS.length) {
          onChange?.({target: {value: TIME_OPTIONS[focusedIndex]}} as React.ChangeEvent<HTMLInputElement>)
          setIsOpen(false)
          anchorRef.current?.querySelector('input')?.blur()
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        anchorRef.current?.querySelector('input')?.blur()
        break
    }
  }, [isOpen, focusedIndex, onChange])

  const handleOptionMouseDown = useCallback(
    (e: React.MouseEvent, timeValue: string) => {
      e.preventDefault()
      onChange?.({target: {value: timeValue}} as React.ChangeEvent<HTMLInputElement>)
    },
    [onChange]
  )

  // Scroll to selected/nearest option when dropdown opens or value changes
  useEffect(() => {
    if (!isOpen || !listRef.current) return
    const index = findNearestOptionIndex(value)
    const item = listRef.current.children[index] as HTMLElement | undefined
    if (item) item.scrollIntoView({block: 'nearest', behavior: 'instant' as ScrollBehavior})
  }, [isOpen, value])

  // Scroll focused item into view on keyboard navigation
  useEffect(() => {
    if (!isOpen || !listRef.current || focusedIndex < 0) return
    const item = listRef.current.children[focusedIndex] as HTMLElement | undefined
    if (item) item.scrollIntoView({block: 'nearest', behavior: 'instant' as ScrollBehavior})
  }, [isOpen, focusedIndex])

  // Close dropdown on external scroll
  useEffect(() => {
    if (!isOpen) return
    const handleScroll = (e: Event) => {
      if (listRef.current?.contains(e.target as Node)) return
      setIsOpen(false)
      anchorRef.current?.querySelector('input')?.blur()
    }
    window.addEventListener('scroll', handleScroll, true)
    return () => window.removeEventListener('scroll', handleScroll, true)
  }, [isOpen])

  if (isDisabled) {
    return (
      <TimeInput value={value} onChange={onChange} onFocus={onFocus} onBlur={onBlur} isDisabled {...rest} />
    )
  }

  return (
    <div
      ref={anchorRef}
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      onKeyDown={handleKeyDown}
    >
      <TimeInput
        value={value}
        onChange={onChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        isDisabled={isDisabled}
        {...rest}
      />
      {isOpen &&
        createPortal(
          <div
            className="fixed z-50 bg-white border border-border rounded-lg shadow-md overflow-hidden"
            style={{top: position.top, left: position.left, width: position.width}}
          >
            <div
              ref={listRef}
              role="listbox"
              aria-label="Time options"
              aria-activedescendant={focusedIndex >= 0 ? `time-option-${TIME_OPTIONS[focusedIndex]}` : undefined}
              className="overflow-y-auto max-h-[240px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {TIME_OPTIONS.map((option, index) => (
                <Button
                  key={option}
                  id={`time-option-${option}`}
                  type="button"
                  role="option"
                  aria-selected={option === value}
                  colorClass={`text-foreground transition-colors ${
                    option === value ? 'bg-container_background_selected' : 'hover:bg-neutral-100'
                  }`}
                  className="!h-auto w-full !rounded-none !justify-start !font-normal pl-4 pr-2"
                  onMouseDown={(e) => handleOptionMouseDown(e, option)}
                >
                  <div className={`flex w-full items-center justify-between py-3 text-base leading-6 ${
                    index < TIME_OPTIONS.length - 1 ? 'border-b border-border' : ''
                  }`}>
                    <span>{option}</span>
                    {option === value && <Icon path={mdiCheck} size="regular" className="shrink-0 text-primary" />}
                  </div>
                </Button>
              ))}
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}
